import Vue from 'vue';
import Vuex from 'vuex';
import Utils from '@/helpers/Utils';
import deals from '@/store/modules/deals';
import parametric from '@/store/modules/parametric';
import router from '@/router';
import api, { setAuthHeader } from '@/api';
import { isProfileComplete } from '@/helpers/authRedirect';
import { baseURL, csrfCookie, goOut, hasActiveToken } from './settings';

Vue.use(Vuex);

let getUserDataInFlight = null;
let logoutInFlight = null;

const token = Utils.getCookie('tekta_access_token') || null;

setAuthHeader(token);

const store = new Vuex.Store({
    modules: {
        deals,
        parametric
    },

    strict: process.env.NODE_ENV !== 'production',
    /**
     * Глобальное хранилище для всего приложения
     * Доступ только через геттеры, изменение через мутацию.
     */
    state : {
        /* Телефонный номер пользователя */
        phoneNumber       : '',
        /* Данные о пользователе */
        user              : {},
        /* Количество оставшихся попыток авторизации */
        loginAttemptCount : Number(sessionStorage.getItem('loginAttemptCount')) || 5,
        /* Фамилия пользователя */
        surname           : null,
        /* текст пользовательского соглашения */
        agreement         : null,
        /* Уникальный идентификатор контрагента в CRM */
        userId            : localStorage.getItem('tektaUserId') || null,
        token             : Utils.getCookie('tekta_access_token') || null,
        /* список ЖК */
        districts         : [],
        /* ссылки на сторонние ресурсы компании */
        linksSettings     : {},
        /* информация по проектам */
        projects          : [],
        isActiveFirstPopup: false,
        dataFirstPopup    : {},
        /* новости / акции в сайдбаре */
        sidebarNews       : null,
        /* агентства недвижимости */
        userData          : {},
        /* Профиль уже запрашивали в текущей сессии */
        userDataLoaded    : false
    },

    getters: {
        GET_PHONE(state) {
            return state.phoneNumber;
        },

        GET_LINKS_SETTINGS(state) {
            return state.linksSettings;
        },

        GET_AGREEMENT(state) {
            return state.agreement;
        },

        GET_TOKEN(state) {
            return state.token;
        },


        GET_LOGIN_ATTEMPT_COUNT(state) {
            return state.loginAttemptCount;
        },

        GET_IS_AUTHENTICATED(state) {
            return Boolean(state.token);
        },

        GET_USER_ID(state) {
            return state.userId;
        },

        GET_USER(state) {
            if (state.user) {
                return state.user;
            }

            const storageData = localStorage.getItem('tektaUser');

            if (!storageData) {
                return null;
            }

            return JSON.parse(storageData);
        },

        GET_USER_NAME(state, getters) {
            if (!getters.GET_USER) {
                return 'Профиль пользователя';
            }

            const { surname, firstName, middleName } = getters.GET_USER;

            if (!surname && !firstName && !middleName) {
                return 'Профиль пользователя';
            }

            let result = `${surname}`;

            if (firstName) {
                result = `${result} ${firstName[0]}.`;
            }

            if (middleName) {
                result = `${result} ${middleName[0]}.`;
            }

            return result;
        },

        GET_SURNAME(state) {
            return state.surname;
        },

        GET_DISTRICT_LIST(state) {
            return state.districts;
        },

        GET_PROJECTS(state) {
            return state.projects;
        },

        GET_IS_FIRST_POPUP_STATUS(state) {
            return state.isActiveFirstPopup;
        },

        GET_DATA_FIRST_POPUP(state) {
            return state.dataFirstPopup;
        },

        GET_SIDEBAR_NEWS(state) {
            return state.sidebarNews;
        },
        GET_USER_DATA(state) {
            return state.userData;
        },

        GET_IS_USER_DATA_LOADED(state) {
            return state.userDataLoaded;
        },

        GET_IS_PROFILE_COMPLETE(state) {
            return isProfileComplete(state.userData);
        }
    },

    actions: {
        /**
         * Создание CSRF-токена чтобы общаться с CRM
         */
        async createCSRFToken() {
            const url = csrfCookie;
            const config = {
                url,
                baseURL
            };

            return await api.get(url, config)
                .then(function (response) {
                    return response;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Получает ссылки на сторонние ресурсы
         * @param commit {function} - метод запуска мутации
         * @return {Promise<unknown>}
         */
        async getLinksSettings({commit}) {
            const url = '/api/broker/linksSettings';
            const config = {
                url,
                baseURL
            };

            return await api.get(url, config)
                .then(function ({data}) {
                    commit('SET_LINKS_SETTINGS', data?.data);

                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Получает текст политики кофиденциальности
         * @param commit {function} - метод запуска мутации
         * @return {Promise<unknown>}
         */
        async getAgreementText({commit}) {
            const url = '/api/broker/agreementText';
            const config = {
                url,
                baseURL
            };

            return await api.get(url, config)
                .then(function ({data}) {
                    commit('SET_AGREEMENT', data.text);

                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Проверяет доступность SMS и звонка для авторизации
         */
        async checkAvailableAuth() {
            const url = '/api/settings/getAuth';
            const config = {
                url,
                baseURL
            };

            return await api.get(url, config)
                .then(function (response) {
                    const payload = response?.data?.data || {};

                    return {
                        isSmsAvailable : payload.isSmsAvailable === true,
                        isCallAvailable: payload.isCallAvailable === true,
                        errorTitle     : payload.errorTitle,
                        errorText      : payload.errorBrokerText || null
                    };
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Отправка SMS с кодом пользователю при авторизации
         * @param commit {function} - метод запуска мутации
         * @param phone {String} - телефон пользователя
         * @param captchaToken {String} - токен
         */
        async sendSMS({commit}, {phone, captchaToken = ''}) {
            commit('SET_PHONE', phone);

            const data = {
                phone,
                captchaToken,
                cabinet: 'broker'
            };
     
            return await api.post('/auth/sms/send', data)
                .then(function (response) {
                    return response;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Отправка звонка пользователю при авторизации
         * @param commit {function} - метод запуска мутации
         * @param phone {String} - телефон пользователя
         */
        async sendCall({commit}, {phone, captchaToken = ''}) {
            commit('SET_PHONE', phone);

            const data = {
                phone,
                captchaToken,
                cabinet: 'broker'
            };

            return await api.post('/auth/call/send', data)
                .then(function (response) {
                    return response;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Проверяет введённый SMS код пользователем
         * @param phone {String} - телефон пользователя
         * @param smsCode {Number} - код из смс
         */
        async checkSMSCode(_, {phone, smsCode, codeType = 'sms'}) {
            const data = {
                phone,
                smsCode,
                codeType,
                cabinet: 'broker'
            };

            return await api.post('/auth/code/verify', data)
                .then(function (response) {
                    const {data} = response;

                    return response;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Поиск контрагента в БД CRM по номеру телефона
         * @param state {Object} - корневое состояние. Только для чтения
         * @param phone {String} - телефон пользователя
         */
        async findCounterpart(_, {phone}) {
            const data = {
                phone,
                cabinet: 'broker'
            };

            return await api.post('/auth/client/search', data)
                .then(function (response) {
                    return response;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Создаёт контрагента
         * @param state {Object} - корневое состояние. Только для чтения
         * @param phone {String} - номер телефона
         * @param firstName {String} - имя
         * @param surName {String} - фамилия
         * @param middleName {String} - отчество
         * @param email {String} - адрес почты
         * @param agency {String} - наименование агентства
         */
        async createCounterpart({state}, {phone, firstName, surName, middleName, email, agency}) {
            const uuid = Utils.getCookie('mindboxDeviceUUID');
            const data = {
                cabinet: 'broker',
                uuid   : uuid || null,
                phone,
                firstName,
                surName,
                middleName,
                email,
                agency
            };

            return await api.post('/auth/register', data)
                .then(function (response) {
                    return response;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Проверка фамилии контрагента в CRM
         * @param state {Object} - корневое состояние. Только для чтения
         * @param surName {String} - фамилия клиента
         */
        async surnameVerification({state}, {surName}) {
            const data = {
                userId : state.userId,
                phone  : state.phoneNumber,
                cabinet: 'broker',
                surName
            };

            return await api.post('/auth/surname/verify', data)
                .then(function (response) {
                    return response;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Получает JWT-токен авторизации
         * @param dispatch {Function} - метод запуска action
         * @param state {Object} - корневое состояние. Только для чтения
         * @param commit {function} - метод запуска мутации
         */
        async getToken({state, commit}) {
            const uuid = Utils.getCookie('mindboxDeviceUUID');
            const data = {
                userId : state.userId,
                phone  : state.phoneNumber,
                uuid   : uuid || null,
                cabinet: 'broker'
            };

            return await api.post('/auth/token/issue', data)
                .then(function (response) {
                    const {data} = response;

                    if (data.data && (data.data.surname || data.data.firstName || data.data.middleName)) {
                        commit('SET_USER', data.data);
                    } else {
                        commit('SET_USER', {
                            surname   : 'Иванов',
                            firstName : 'Иван',
                            middleName: 'Иванович'
                        });
                    }

                    return response;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Сохраняем токены
         * @param commit {function} - метод запуска мутации
         * @param accessToken {String} - токен
         * @param expiresIn {Number} - время жизни токена
         * @param expiresInRefresh {Number} - время жизни refresh токена
         */
        setTokens({commit}, {accessToken, expiresIn, expiresInRefresh}) {
            return new Promise(resolve => {
                commit('SET_TOKEN', accessToken);

                // Записываем токены CRM в куки expiresIn
                accessToken && Utils.setCookie('tekta_access_token', accessToken, {
                    'max-age': expiresIn * 60,
                    'secure' : true
                });

                // Сохраняем время жизни токена (переводим минуты в миллисекунды и добавляем текущую дату)
                expiresIn && localStorage.setItem('tekta_token_expires_on', String((expiresIn * 60 * 1000) + Date.now()));

                // Записываем время жизни refresh-токена (переводим минуты в миллисекунды и добавляем текущую дату)
                expiresInRefresh && localStorage.setItem('expiresInRefresh', String((expiresInRefresh * 60 * 1000) + Date.now()));

                setAuthHeader(accessToken);

                resolve(true);
            });
        },

        /**
         * Обновляет access-токен по refresh-cookie
         * @param dispatch {Function} - метод запуска action
         */
        async refreshAllTokens({dispatch}) {
            try {
                const {data} = await api.post('/auth/token/refresh', null);

                if (!data?.data?.accessToken || data?.data?.messageCode === 3) {
                    throw new Error('Ошибка при обновлении токенов');
                }

                await dispatch('setTokens', {
                    accessToken     : data?.data?.accessToken,
                    expiresIn       : data?.data?.expiresIn,
                    expiresInRefresh: data?.data?.expiresInRefresh,
                }).catch((error) => {
                    throw error;
                });

                return true;
            } catch (error) {
                console.error('Ошибка при сбросе токенов');

                throw error;
            }
        },

        /**
         * Записывает в state userId
         * @param commit {function} - метод запуска мутации
         * @param userId {String} - уникальный идентификатор контрагента в CRM
         */
        addUserId({commit}, {userId}) {
            localStorage.setItem('tektaUserId', userId);
            commit('SET_USER_ID', userId);
        },

        /**
         * Записывает в state фамилию пользователя
         * @param commit {function} - метод запуска мутации
         * @param surname {string} - фамилия
         */
        addSurname({commit}, surname) {
            commit('SET_SURNAME', surname);
        },

        /**
         * Изменяет количество ошибок(попыток) для авторизации
         * @param state {Object} - корневое состояние. Только для чтения
         * @param commit {function} - метод запуска мутации
         */
        changeCountLoginAttempt({state, commit}) {
            const newCount = state.loginAttemptCount - 1;

            sessionStorage.setItem('loginAttemptCount', String(newCount));

            // Если попыток на авторизацию не осталось, указываем время начала блокировки
            if (newCount <= 0) {
                const currentTime = new Date();

                sessionStorage.setItem('loginAttemptBlockStart', String(currentTime));
            }

            commit('SET_LOGIN_ATTEMPT_COUNT', newCount);
        },

        /**
         * Запрос данных контрагента
         * @param state {Object} - корневое состояние. Только для чтения
         * @param commit {function} - метод запуска мутации
         */
        async getUserData({state, commit}) {
            if (state.userDataLoaded) {
                return;
            }

            if (getUserDataInFlight) {
                return getUserDataInFlight;
            }

            getUserDataInFlight = (async () => {
                const data = {
                    userId: state.userId
                };

                return await api.post('/user', data)
                    .then(function (response) {
                        const {data} = response;
                        const profile = (data && data.data) || {};

                        if (profile.firstName || profile.middleName || profile.lastName) {
                            /* sic (CRM) */
                            commit('SET_USER', {
                                surname   : profile.lastName,
                                firstName : profile.firstName,
                                middleName: profile.middleName
                            });
                        }

                        commit('SET_USER_DATA', profile);

                        return response;
                    })
                    .catch(function (error) {
                        throw error;
                    });
            })();

            try {
                return await getUserDataInFlight;
            } finally {
                getUserDataInFlight = null;
            }
        },

        /**
         * Дозаполнение профиля пользователя
         * @param state {Object} - корневое состояние. Только для чтения
         * @param commit {function} - метод запуска мутации
         * @param firstName {String} - имя
         * @param lastName {String} - фамилия
         * @param middleName {String} - отчество
         * @param email {String} - адрес почты
         */
        async fillProfile({state, commit}, {firstName, lastName, middleName, email, agency}) {
            const data = {
                userId : state.userId,
                surName: lastName,
                firstName,
                middleName,
                email,
                agency
            };

            return await api.post('/user/profile', data)
                .then(function (response) {
                    const payload = response.data && response.data.data;

                    if (payload && payload.messageCode === 1 && isProfileComplete(payload)) {
                        commit('SET_USER_DATA', payload);
                        commit('SET_USER', {
                            surname   : payload.lastName || payload.surname,
                            firstName : payload.firstName,
                            middleName: payload.middleName
                        });
                    }

                    return response;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Выход из аккаунта пользователя.
         * Запрос уходит только при живом токене и до очистки cookie.
         */
        async logout({commit}) {
            if (logoutInFlight) {
                return logoutInFlight;
            }

            if (!hasActiveToken()) {
                return;
            }

            logoutInFlight = (async () => {
                try {
                    return await api.post('/auth/logout', null);
                } finally {
                    commit('SET_LOGOUT_USER');
                    setAuthHeader(null);
                    logoutInFlight = null;
                }
            })();

            return logoutInFlight;
        },

        /**
         * Проверка пользователя на уникальность
         * @param state {Object} - корневое состояние. Только для чтения
         * @param phone {String} - телефон пользователя
         * @param projectId {String} - идентификатор проекта
         * @param clientName {String} - имя клиента
         * @param clientSurname {String} - фамилия клиента
         */
        async checkUserUnique({state} , {
            phone,
            projectId,
            clientName,
            clientSurname
        }) {
            const data = {
                userId: state.userId,
                phone,
                projectId,
                clientName,
                clientSurname
            };

            return await api.post('/clients/unique-check', data)
                .then(function (response) {
                    return response;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Запрос предназначен для получения списка уже существующих записей на визит по указанному агенту.
         * @param state {Object} - корневое состояние. Только для чтения
         * @param projectId {string} - id проекта для записи
         * @return {Promise<unknown>}
         */
        async getAppointments({ state }) {
            const data = {
                userId: state.userId,
            };

            return await api.post('/visits', data)
                .then(function ({data}) {
                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        async getFAQList() {
            const url = '/api/broker/faqList';
            const config = {
                url,
                baseURL
            };

            return await api.get(url, config)
                .then(function ({data}) {
                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Запрос предназначен для получения дат, в которые есть свободные слоты для записи на визит.
         * @param state {Object} - корневое состояние. Только для чтения
         * @param projectId {string} - id проекта для записи
         * @return {Promise<unknown>}
         */
        async getAppointmentsDates({ state }, { projectId}) {
            if (!projectId) {
                throw new Error('Отсутствует projectId для получения дат на запись');
            }

            const data = {
                userId      : state.userId,
                numberOfDays: 14,
                projectId,
            };


            return await api.post('/visits/slot-days', data)
                .then(function ({data}) {
                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Запрос предназначен для получения свободных слотов для записи на визит на выбранную дату.
         * @param state {Object} - корневое состояние. Только для чтения
         * @param projectId {string} - id проекта для записи
         * @param date {string} - дата для временных слотов. "YYYY-MM-DD". "2024-08-14"
         * @return {Promise<unknown>}
         */
        async getAppointmentsTimeSlots({ state }, { projectId, date}) {
            if (!projectId) {
                throw new Error('Отсутствует projectId для получения временных слотов на запись');
            }

            if (!date) {
                throw new Error('Отсутствует дата для получения слотов на запись');
            }

            const data = {
                userId: state.userId,
                projectId,
                date,
            };

            return await api.post('/visits/slots', data)
                .then(function ({data}) {
                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Запрос предназначен для создания агентом записи на визит на выбранные дату, время или для изменения/удаления существующего визита.
         * @param state {Object} - корневое состояние. Только для чтения
         * @param projectId {string} - id проекта для записи
         * @param clientPhone {string} - Телефон клиента
         * @param visitStartTime {string} - дата + время для записи. "YYYY-MM-DD hh:mm". "2024-08-14 16:30"
         * @param clientName {string} - имя клиента
         * @param clientSurname {string} - фамилия клиента
         * @param comment {string} - комментарий
         * @param agency {string} - название агентства
         * @return {Promise<unknown>}
         */
        async createAppointment({ state }, {
            projectId, visitStartTime, clientPhone, clientName, clientSurname, comment, agency
        }) {
            if (!projectId) {
                throw new Error('Отсутствует projectId для создания записи');
            }

            if (!visitStartTime) {
                throw new Error('Отсутствует дата и/или время для создания записи');
            }

            const data = {
                userId    : state.userId,
                actionCode: 1,
                projectId,
                visitStartTime,
                clientPhone,
                clientName,
                clientSurname,
                comment,
                agency
            };

            return await api.post('/visits/record', data)
                .then(function ({data}) {
                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Запрос предназначен для создания агентом записи на визит на выбранные дату, время или для изменения/удаления существующего визита.
         * @param state {Object} - корневое состояние. Только для чтения
         * @param visitRecordId {string} - id существующей записи на презентацию
         * @return {Promise<unknown>}
         */
        async deleteAppointment({ state }, { visitRecordId }) {
            if (!visitRecordId) {
                throw new Error('Отсутствует visitRecordId для удаления записи');
            }

            const data = {
                userId    : state.userId,
                actionCode: 0,
                visitRecordId
            };

            return await api.post('/visits/record', data)
                .then(function ({data}) {
                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Запрос предназначен для создания агентом записи на визит на выбранные дату, время или для изменения/удаления существующего визита.
         * @param state {Object} - корневое состояние. Только для чтения
         * @param visitStartTime {string} - дата + время для записи. "YYYY-MM-DD hh:mm". "2024-08-14 16:30"
         * @param visitRecordId {string} - id существующей записи на презентацию
         * @param comment {string} - комментарий
         * @return {Promise<unknown>}
         */
        async changeAppointment({ state }, { visitStartTime, visitRecordId, comment }) {
            if (!visitRecordId) {
                throw new Error('Отсутствует visitRecordId для удаления записи');
            }

            if (!visitStartTime) {
                throw new Error('Отсутствует дата и/или время для изменения записи');
            }

            const data = {
                userId    : state.userId,
                actionCode: 2,
                visitStartTime,
                visitRecordId,
                comment
            };

            return await api.post('/visits/record', data)
                .then(function ({data}) {
                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /* ЗАПРОСЫ К НАШЕМУ БЭКЕНДУ */

        /**
         * Получение списка жилых комплексов
         * @param commit {function} - метод запуска мутации
         */
        async getComplexes({commit}) {
            const url =  `/api/estate/complexes`;
            const config = {
                url,
                baseURL
            };

            return await api.get(url, config)
                .then(function (response) {
                    const {data} = response;

                    if (data) {
                        data.forEach((complex) => {
                            if (!complex.basis_id) {
                                complex.basis_id = complex.id;
                            }
                        });
                    }

                    commit('SET_DISTRICT_LIST', data);

                    return data;
                })
                .catch(function (error) {
                    if (error?.response?.status === 401) {
                        goOut();
                    }

                    throw error;
                });
        },

        /**
         * Получает информацию о проектах
         */
        async getProjectsInfo({commit}) {
            const url = '/api/estate/complexesInfo';
            const config = {
                url,
                baseURL
            };

            await api.get(url, config)
                .then(function ({data}) {
                    commit('SET_PROJECTS', data);
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Получает информацию для информационного попапа
         */
        async getInfoFromFirstPopup({state, commit, dispatch}) {
            if (!state.districts?.length) {
                await dispatch('getComplexes');
            }

            const url = '/api/settings/getPopup';
            const config = {
                url,
                baseURL
            };

            await api.get(url, config)
                .then(function ({data}) {
                    commit('SET_INFO_FIRST_POPUP', data);
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Отправка формы обратной связи
         * @param name {String} - имя брокера
         * @param phone {String} - телефон брокера
         */
        async sendFeedback(_, {
            name,
            phone
        }) {
            const url =  `/api/broker/sendFeedbackForm`;
            const data = {
                name,
                phone
            };
            const config = {
                url,
                baseURL
            };

            return await api.post(url, data, config)
                .then(function (response) {
                    return response;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Отправка запроса на брокер-тур
         * @param name {String} - имя брокера
         * @param phone {String} - телефон брокера
         * @param email {String} - почта брокера
         * @param projectId {String} - уникальный идентификатор проекта в CRM
         * @param peopleCount {Number} - количество гостей
         */
        async sendBrokerTourRequest(_, {
            name,
            phone,
            email,
            projectId,
            peopleCount
        }) {
            const url =  `/api/broker/sendBrokerTourForm`;
            const data = {
                name,
                phone,
                email,
                project_id      : projectId,
                number_of_people: peopleCount
            };
            const config = {
                url,
                baseURL
            };

            return await api.post(url, data, config)
                .then(function (response) {
                    return response;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Получает ссылку на pdf страницу
         * @param type {string} - refund - заявление на возврат, plan - план квартиры
         * @param data {object} - данные для формирования pdf страницы
         * @return {Promise<unknown>}
         */
        async getPdfPage({ }, { type = 'refund', data }) {
            let url = `/api/${type}/print`;
            const config = {
                url,
                baseURL
            };

            return await api.post(url, data, config)
                .then(function ({data}) {
                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Получаем новость для показа в сайдбаре
         */
        async getSidebarNewsData({commit}) {
            const url = '/api/news/sidebar/broker';
            // const url = '/tests/sidebar-news.json';

            const config = {
                url    : url,
                baseURL: '/'
            };

            return await api.get(url, config)
                .then(function ({data}) {
                    commit('SET_SIDEBAR_NEWS', data.data);
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Получаем вопросы
         */
        async getQuestionsData() {
            // const versionRandom = Utils.generateRandomValue() + Utils.generateRandomValue() + Utils.generateRandomValue();
            // const url = `/tests/questions.json?id=${versionRandom}`;
            const url = '/api/questions/broker';

            const config = {
                url    : url,
                baseURL: '/'
            };

            return await fetch(url, config)
                .then((response) => response.json())
                .then((data) => {
                    return data;
                })
                .catch((error) => {
                    console.error(error);
                    throw error;
                });
        },

        /**
         * Получаем список новостей
         * @returns {Promise<unknown>}
         */
        async getNewsList() {
            const url = `/api/broker/news`;
            // const url = `/tests/article-list-new.json`;
            const config = {
                url    : url,
                baseURL: '/'
            };

            return await api.get(url, config)
                .then(function ({data}) {
                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Получает статью по slug
         * @return {Promise<unknown>}
         */
        async getNewsForSlug({commit}, slug) {
            let url = `/api/broker/news/${slug}`;
            // let url = `/tests/news.json`;
            const config = {
                url    : url,
                baseURL: '/'
            };

            return await api.get(url, config)
                .then(function ({data}) {
                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        async sendQuestionsData({state}, data) {
            // const versionRandom = Utils.generateRandomValue() + Utils.generateRandomValue() + Utils.generateRandomValue();
            // const url = `/tests/questions-result.json?id=${versionRandom}`;

            const url = '/api/questions/broker';
            const config = {
                url,
                baseURL
            };

            return await api.post(url, data.result, config)
                .then(function ({data}) {
                    return data;
                })
                .catch(function (error) {
                    throw error;
                });
        },

        /**
         * Отправка сообщения об ошибке
         * @param state {Object} - корневое состояние. Только для чтения
         * @param {FormData} data - данные формы
         * @returns 
         */
        async sendProblemsReport({state}, data) {
            // const versionRandom = Utils.generateRandomValue() + Utils.generateRandomValue() + Utils.generateRandomValue();
            // const url = `/tests/report-problem-result.json?id=${versionRandom}`;
            const url = '/api/form/support';

            const config = {
                url,
                baseURL,
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
            };

            return await api.post(url, data, config)
                .then((response) => {
                    const { data } = response;

                    return data;
                })
                .catch((error) => {
                    console.error(error);
                    throw error;
                });
        }
    },

    mutations: {
        SET_PHONE(state, payload) {
            state.phoneNumber = payload;
        },
        SET_TOKEN(state, token) {
            state.token = token;
        },
        SET_LOGIN_ATTEMPT_COUNT(state, count) {
            state.loginAttemptCount = count;
        },
        SET_AGREEMENT(state, agreement) {
            state.agreement = agreement;
        },
        SET_LINKS_SETTINGS(state, data) {
            const { telegram_channel, presentation_link, regulations_link } = data;

            state.linksSettings = {
                telegramChannel : telegram_channel,
                presentationLink: presentation_link,
                regulationsLink : regulations_link
            };
        },
        SET_USER_ID(state, userId) {
            state.userId = userId;
        },
        SET_USER(state, userData) {
            const { surname, firstName, middleName } = userData;
            const fullName = `${surname} ${firstName} ${middleName}`;
            const user = {
                surname,
                firstName,
                middleName,
                fullName
            };

            localStorage.setItem('tektaUser', JSON.stringify(user));

            state.user = user;
        },
        SET_SURNAME(state, surname) {
            state.surname = surname;
        },
        SET_LOGOUT_USER(state) {
            state.userId = null;
            state.user = null;
            state.surname = null;
            state.phoneNumber = null;
            state.agreement = null;
            state.loginAttemptCount = null;
            state.token = null;
            state.userData = {};
            state.userDataLoaded = false;
            Utils.deleteCookie('tekta_access_token');
            Utils.deleteCookie('tekta_refresh_token');
            Utils.deleteCookie('XSRF-TOKEN');
            localStorage.removeItem('tektaUser');
            localStorage.removeItem('tektaUserId');
            localStorage.removeItem('tekta_token_expires_on');
            localStorage.removeItem('expiresInRefresh');
        },
        SET_DISTRICT_LIST(state, districts) {
            state.districts = districts;
        },
        SET_PROJECTS(state, projects) {
            state.projects = projects;
        },
        SET_INFO_FIRST_POPUP(state, info) {
            const { data } = info;
            const needAuth = Boolean(router.currentRoute.meta.requiresAuth);

            state.isActiveFirstPopup = data?.enable && needAuth || false;

            state.dataFirstPopup = {
                title : data?.title,
                text  : data?.text,
                button: data?.button,
            };
        },
        SET_SIDEBAR_NEWS(state, news) {
            state.sidebarNews = news;
        },
        SET_USER_DATA(state, data) {
            const userDataKeys = Object.keys(data || {});
            const currentAgencies = (state.userData && state.userData.agencies) || [];
            const nextAgencies = Array.isArray(data.agencies)
                ? data.agencies
                : currentAgencies;

            state.userData = userDataKeys.length > 0 ? {
                middleName: data.middleName || '',
                firstName : data.firstName || '',
                surname   : data.lastName || data.surname || '',
                phone     : data.phone || '',
                email     : data.email || '',
                agencies  : nextAgencies,
            } : {};

            state.userDataLoaded = userDataKeys.length > 0;
        },
    },
});

export default store;
